<script lang="ts" setup>
import AUApi from "@/api";
import { Globals } from "@/Globals";
import { ref, onMounted, reactive } from "vue";
import { begriffBezeichnung, checkBegriff, globalsBegriffMemo } from "@/local";

import { getKennungValueDisplay, getKennungValue, getKennungTitle } from "@/DataHelper";
import { showSnackbar } from "@/UIHelper";
import Constants from "@/Constants";
import {} from "@/CalcHelperStatic";
import { BaseSetup, mountedBase, onDelete, onSave } from "@/DataLoaderStatic";
import { getSiteData, goBack } from "@/NavigationHelper";
import { json } from "stream/consumers";
let auftrag = ref<string>("");
let kst = ref<string>("");
let taetigkeit = ref<string>("");
let letzteBuchung: string = "";
let letzteBuchungUm: string = "";
let letzteBuchungAic = ref<string>("");
let showMobil = ref<boolean>(false);
const buchungsText = ref<string>("");

const siteData = getSiteData();
const emit = defineEmits<{
  back: [];
  "update:modelValue": [value: any];
  //calculated: [data?: any];
}>();

let query = "Web_SoftwareTerminal";
if (siteData?.query) {
  query = siteData?.query;
} else if (siteData?.keinBDEAntrag || siteData?.admin) {
  query = "Web_SoftwareTerminal";
} else if (checkBegriff("Web_NacherfassungBDE")) {
  query = "WebAntragStempelungen";
}

let zeitbereich = AUApi.Current.zeitbereich;
if (siteData?.zeitbereich && zeitbereich.von) {
  zeitbereich = siteData.zeitbereich;
} else if (siteData?.zeitbereich && typeof siteData.zeitbereich === "string") {
  zeitbereich = JSON.parse(siteData.zeitbereich as string);
}
let varUebergabe: VarUebergabe[] | undefined;
if (siteData?.varUbergabe?.length) varUebergabe = siteData?.varUbergabe;
if (typeof siteData?.varUbergabe === "string")
  varUebergabe = JSON.parse(siteData.varUbergabe as string);

const pageParams: DataPageParameter = {
  zeitbereich: zeitbereich,
  insertMode: false,
  varUebergabe: varUebergabe,
  query,
  emitBack: () => emit("back"),
};

const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));

onMounted(async () => {
  try {
    uiData.isValueChangeTrackerDisabled = true;
    await mountedBase(uiData, pageParams, false);
    initialize();
    uiData.initialized = true;
  } catch (error) {
    console.error(error);
  }
});
const letzteBuchungText = ref("--");

function initialize() {
  letzteBuchungText.value = begriffBezeichnung("Web_letzteBuchung");
  if (uiData.data.length == 0) throw new Error("no datarow found!");
  var row = uiData.data[0];
  letzteBuchung = getKennungValueDisplay(row, "BDE", uiData.columns);
  letzteBuchungUm = getKennungValueDisplay(row, "Uhrzeit", uiData.columns);
  if (letzteBuchungUm) {
    buchungsText.value = begriffBezeichnung("Web_letzteBuchung") + " " + letzteBuchungUm;
  }
  if (letzteBuchung) {
    buchungsText.value = buchungsText.value + " - " + letzteBuchung;
  }
  letzteBuchungAic.value = getKennungValue(row, "BDEAIC", uiData.columns);
  if (Constants.isMobile) {
    const auftragHandy = getKennungTitle("Auftrag_Handy", uiData.columns);
    if (auftragHandy) {
      showMobil.value = true;
    }
  }
}

async function save() {
  if (auftrag.value || kst.value || taetigkeit.value) {
    // eines MUSS vorhanden sein!
    const res = await onSave(
      uiData,
      pageParams,
      0,
      "",
      zeitbereich,
      pageParams.aic,
      true,
      pageParams.varUebergabe
    );
    const updateData = res.data;
    siteData.returnedData = updateData;
    pageParams.emitBack(updateData);
  } else {
    const auftragText = getKennungTitle("INSTRUCTION", uiData.columns);
    const kstText = getKennungTitle("COST_CENTRE", uiData.columns);
    const taetigkeitText = getKennungTitle("ACTIVITY", uiData.columns);
    let meldung = globalsBegriffMemo(
      Globals.Begriff,
      "WEB_SpeichernNichtMoeglich",
      "Speichern kann nicht durchgeführt werden da folgende Daten leer sind"
    );
    meldung = meldung + ": " + auftragText + ", " + kstText + ", " + taetigkeitText;
    showSnackbar({
      text: meldung,
      color: "warning",
    });
  }
}

function bewDelete(aic: any) {
  if (aic) {
    onDelete(uiData, pageParams, aic, "");
    letzteBuchung = "";
    letzteBuchungAic.value = "";
    letzteBuchungUm = "";
  }
}
</script>
<template>
  <v-card :loading="uiData.showLoading">
    <v-card-title color="grey lighten-5">
      <v-banner
        v-if="buchungsText"
        color="deep-purple-accent-4"
        icon="mdi-checkbox-marked-outline"
        lines="one"
      >
        <v-banner-text>
          {{ buchungsText }}
        </v-banner-text>

        <template v-slot:actions>
          <v-btn
            v-if="letzteBuchungAic"
            icon="mdi-delete"
            size="small"
            @click="bewDelete(letzteBuchungAic)"
            variant="text"
            color="red"
          >
          </v-btn>
        </template>
      </v-banner>
    </v-card-title>
    <v-card-text v-if="uiData.initialized">
      <EditComboBox
        v-if="!showMobil"
        :modelValue="auftrag"
        @update:modelValue="auftrag = $event"
        kennung="INSTRUCTION"
        :queryZeitbereich="zeitbereich"
      />
      <EditComboBox
        v-if="showMobil"
        :modelValue="auftrag"
        @update:modelValue="auftrag = $event"
        kennung="Auftrag_Handy"
        :queryZeitbereich="zeitbereich"
      />
      <EditComboBox
        :modelValue="taetigkeit"
        @update:modelValue="taetigkeit = $event"
        kennung="ACTIVITY"
        :queryZeitbereich="zeitbereich"
      />
      <EditComboBox
        :modelValue="kst"
        @update:modelValue="kst = $event"
        kennung="COST_CENTRE"
        :queryZeitbereich="zeitbereich"
      />
      <EditTextArea kennung="Memo" />
    </v-card-text>

    <CommonBaseButtons
      :deleteVisible="false"
      :createVisible="false"
      :refreshVisible="false"
      :backVisible="false"
      :saveVisible="true"
      @save="save"
    >
    </CommonBaseButtons>
  </v-card>
</template>
<style scoped></style>
